import { WarrantProgramDocument } from '../../../WarrantProgram.graphql';
import { GqlWarrantProgram } from '../../../../../graphql/base-types.graphql';
import { Alert } from '@startuptools/ui/Alert';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Trans, useLingui } from '@lingui/react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Link } from '@startuptools/ui/Link';
import { BigNumber } from 'bignumber.js';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import { useWpSetVolatilityMutation } from '../../../../../graphql/react-operations';
import { useStepper } from '../../../../../components/steppers/common/StepperContext';
import { useStepperStepContent } from '../../../../../components/steppers/common/StepperStepContentContext';
import { ErrorDisplay } from '../../../../../components/error-display/ErrorDisplay';

type FormValues = Pick<GqlWarrantProgram, 'volatility'>;

export const WpStepVolatility = ({
  warrantProgram,
}: {
  warrantProgram: Pick<GqlWarrantProgram, 'id' | 'volatility' | 'companyId'>;
}) => {
  const { i18n } = useLingui();
  const { stepNext } = useStepper();
  const { validatorStatus } = useStepperStepContent();
  // eslint-disable-next-line sut/loading-mutation
  const [doSubmit] = useWpSetVolatilityMutation({ refetchQueries: [WarrantProgramDocument] });
  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: { volatility: new BigNumber(warrantProgram.volatility ?? '0.3').times(100).toString() },
  });

  const onSubmit: SubmitHandler<FormValues> = async values => {
    const volatility = new BigNumber(values.volatility ?? '30').div(100).toString();
    await doSubmit({
      variables: { companyId: warrantProgram.companyId, input: { id: warrantProgram.id, volatility } },
    });
    return stepNext();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={3}>
        <Typography level="h4">
          <Trans id="Volatility" />
        </Typography>

        <Alert>
          <Typography component="span" level="body-sm">
            <Trans
              id="Volatility is a measure of much the value of the underlying share fluctuates. This is the single most important parameter in a warrant program and there is a tax risk of the volatility is set too low. If it is set too high, the warrant will be too expensive. If you are not familiar with the concept, we recommend that you read <articleLink>this article.</articleLink>"
              components={{
                articleLink: (
                  <Link
                    component="a"
                    slotProps={{
                      root: {
                        href: 'https://help.startuptools.org/sv/articles/6132772-teckningsoptioner-steg-for-steg',
                        target: '_blank',
                      },
                    }}
                  />
                ),
              }}
            />
          </Typography>
        </Alert>

        <Controller
          control={control}
          name="volatility"
          render={({ field: { onChange, value, ...field } }) => (
            <FormControl sx={{ maxWidth: 300 }}>
              <FormLabel required>
                <Trans id="Volatility" />
              </FormLabel>
              <NumericFormatInput
                {...field}
                lang={i18n.locale}
                value={value ?? '30'}
                onChange={v => onChange(v.target.value)}
                endDecorator="%"
                required
              />
            </FormControl>
          )}
        />

        {validatorStatus?.hasError && <ErrorDisplay error={validatorStatus.error} />}

        <Box>
          <Button type="submit">
            <Trans id="Save and Continue" />
          </Button>
        </Box>
      </Stack>
    </form>
  );
};
